/**
 * NEW-FEATURE SPOTLIGHT (CLOSEOUT-ESTIMATE-INTEL §D4.10) — generic system.
 *
 * Any shipped feature registers a `feature_announcements` row (feature key,
 * headline, one-liner, target permission). Wrap the feature's button in
 * `<SpotlightTarget featureKey="...">`: on the user's first load after the
 * ship, the button pulses with a callout until clicked or dismissed —
 * acknowledged once per user (`feature_announcement_acks`), never again.
 *
 * Seeded with 'support_copilot_help_button'. Future buttons: add a migration
 * row + wrap the button. Nothing else.
 */
import { type ReactNode, useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

interface Announcement {
    id: string;
    feature_key: string;
    headline: string;
    body: string | null;
}

export function useFeatureSpotlight(featureKey: string): {
    announcement: Announcement | null;
    acknowledge: () => void;
} {
    const { user } = useAuth();
    const qc = useQueryClient();
    const unackedQ = useQuery({
        queryKey: ["feature-announcements", "unacked"],
        queryFn: () => api.get<{ announcements: Announcement[] }>("/api/feature-announcements/unacked"),
        enabled: !!user,
        staleTime: 5 * 60_000,
        retry: false,
    });
    const announcement = unackedQ.data?.announcements.find((a) => a.feature_key === featureKey) ?? null;

    const ackMutation = useMutation({
        mutationFn: (id: string) => api.post(`/api/feature-announcements/${id}/ack`, {}),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["feature-announcements", "unacked"] }),
    });

    const acknowledge = useCallback(() => {
        if (announcement && !ackMutation.isPending) ackMutation.mutate(announcement.id);
    }, [announcement, ackMutation]);

    return { announcement, acknowledge };
}

/**
 * Wrap the feature's trigger. While the announcement is unacked the child
 * pulses and a callout bubble points at it; clicking the child (or the
 * callout's X) acknowledges.
 */
export function SpotlightTarget({ featureKey, children }: { featureKey: string; children: ReactNode }) {
    const { announcement, acknowledge } = useFeatureSpotlight(featureKey);
    const [dismissed, setDismissed] = useState(false);
    const active = announcement != null && !dismissed;

    const handleAck = () => {
        setDismissed(true);
        acknowledge();
    };

    if (!active) return <>{children}</>;

    return (
        // Click ack rides capture so the child's own onClick still runs.
        <div className="relative" onClickCapture={handleAck} data-testid={`spotlight-${featureKey}`}>
            <div className={cn("rounded-md animate-pulse ring-2 ring-primary/70 ring-offset-1 ring-offset-background")}>
                {children}
            </div>
            <div
                role="status"
                // eslint-disable-next-line no-restricted-syntax -- D1: absolutely-positioned overlay above content; the blur is load-bearing
                className="absolute right-0 top-full mt-2 z-[60] w-64 rounded-lg border bg-card/95 backdrop-blur-sm text-card-foreground shadow-xl p-3 text-left"
            >
                <div className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold leading-snug">{announcement.headline}</p>
                        {announcement.body && (
                            <p className="mt-1 text-xs text-muted-foreground leading-snug">{announcement.body}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        aria-label="Dismiss announcement"
                        className="shrink-0 rounded p-0.5 hover:bg-muted"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAck();
                        }}
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
